import { cn } from "@/lib/utils";

interface FileTypeIconProps {
  /** Filename to extract extension from */
  filename?: string;
  /** MIME content type (legacy fallback) */
  contentType?: string;
  /** Additional CSS classes */
  className?: string;
  /** Optional typography override for the extension label. */
  labelClassName?: string;
}

/**
 * Get color scheme (text + background) based on file extension category.
 */
function getExtensionColors(ext: string): string {
  switch (ext) {
    case "pdf":
      return "text-red-700 bg-red-50";
    case "doc":
    case "docx":
      return "text-blue-700 bg-blue-50";
    case "xls":
    case "xlsx":
    case "xlsm":
    case "csv":
      return "text-green-700 bg-green-50";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "svg":
    case "webp":
    case "bmp":
    case "tiff":
    case "tif":
      return "text-purple-700 bg-purple-50";
    case "zip":
    case "asice":
    case "bdoc":
    case "ddoc":
    case "rar":
    case "7z":
    case "tar":
    case "gz":
      return "text-amber-700 bg-amber-50";
    case "dwg":
    case "dxf":
    case "nc1":
      return "text-orange-700 bg-orange-50";
    case "fbx":
      return "text-teal-700 bg-teal-50";
    case "ifc":
      // BIM model — violet, matching the "BIM" source badge in Building Data (WHA-3642).
      return "text-violet-700 bg-violet-50";
    default:
      return "text-gray-600 bg-gray-100";
  }
}

/**
 * Get text size class based on extension length.
 */
function getTextSize(ext: string): string {
  if (ext.length <= 2) return "text-[10px]";
  if (ext.length === 3) return "text-[9px]";
  return "text-[7px]";
}

/**
 * FileTypeIcon - Atom component
 *
 * Displays a small square text badge showing the file extension (e.g., "DOC", "DWG", "XLSX").
 * Color-coded by file type category.
 */
export function FileTypeIcon({
  filename,
  contentType,
  className,
  labelClassName,
}: FileTypeIconProps) {
  // Extract extension from filename
  let ext = "";
  if (filename) {
    const parts = filename.split(".");
    if (parts.length > 1) {
      ext = parts.pop()!.toLowerCase();
    }
  }

  // Fallback label when no extension can be extracted
  const label = ext ? ext.toUpperCase() : "FILE";
  const displayExt = ext || "";
  const colorClasses = getExtensionColors(displayExt);
  const textSize = getTextSize(label);

  return (
    <div
      className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded",
        colorClasses,
        className
      )}
      aria-hidden="true"
      title={label}
    >
      <span className={cn("font-semibold leading-none", textSize, labelClassName)}>{label}</span>
    </div>
  );
}
